import { useMemo, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import { ProductStatusFilter } from 'components/productsAndSerevices/ProductStatusFilter';
import { determineStatusFromEnum } from 'types';
import ProductAndServicesTable from 'components/productsAndSerevices/ProductAndServicesTable';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import { ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { jbColors } from 'theme/jbColors';
import ProductDocumentsToDistributeDialog, {
  DistributeItem,
} from 'components/productsAndSerevices/ProductDocumentsToDistributeDialog';

const filterValues = [
  { id: 1, text: 'In Opening', filterKey: 'In Opening' },
  { id: 2, text: 'Active', filterKey: 'Active' },
  { id: 3, text: 'Inactive', filterKey: 'Inactive' },
];

type ProductsAndServicesProps = {
  partnerId?: string | null;
  brUid?: string | null;
};

export const ProductsAndServices = ({ partnerId: partnerIdRaw, brUid: brUidRaw }: ProductsAndServicesProps) => {
  const partnerId = partnerIdRaw ?? undefined;
  const brUid = brUidRaw ?? undefined;
  const title = 'Overview';

  const tableData = useSelector((state: RootState) => state.serviceAgreements.tableData);
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const { isTableDataLoading } = useSelector((state: RootState) => state.serviceAgreements);
  const { documentToDistribute } = useSelector((state: RootState) => state.productDocuments);
  const [open, setOpen] = useState(false);

  const numberOfItemsByStatus = useMemo(() => {
    const items: ServiceAgreementV2[] = tableData ?? [];
    return {
      'In Opening': items.filter((item) => determineStatusFromEnum(item.agreementStatus) === 'In Opening').length,
      Active: items.filter(
        (item) => determineStatusFromEnum(item.agreementStatus) === 'Active' || !item.agreementStatus,
      ).length,
      Inactive: items.filter((item) => determineStatusFromEnum(item.agreementStatus) === 'Inactive').length,
    };
  }, [tableData]);

  const defaultFilter = useMemo(() => {
    if (selectedServiceAgreement?.agreementStatus) {
      return determineStatusFromEnum(selectedServiceAgreement.agreementStatus);
    }
    if (numberOfItemsByStatus['In Opening'] > 0) return 'In Opening';
    if (numberOfItemsByStatus['Active'] > 0) return 'Active';
    if (numberOfItemsByStatus['Inactive'] > 0) return 'Inactive';
    return '';
  }, [selectedServiceAgreement, numberOfItemsByStatus]);

  const [filter, setFilter] = useState(defaultFilter);
  const [prevDefaultFilter, setPrevDefaultFilter] = useState(defaultFilter);
  if (defaultFilter !== prevDefaultFilter) {
    setPrevDefaultFilter(defaultFilter);
    setFilter(defaultFilter);
  }

  const itemsToDisplay = useMemo(() => {
    const items: ServiceAgreementV2[] = tableData ?? [];
    const filtered = filter
      ? items.filter((item) => determineStatusFromEnum(item?.agreementStatus ?? 'ACTIVE') === filter)
      : items;
    return _.map(_.groupBy(filtered, 'family'), (tableRows, family) => ({ family, tableRows }));
  }, [tableData, filter]);

  const handleStatusFilterClick = (filterKey: string) => {
    if (filterKey === filter) {
      setFilter('');
      return;
    }

    setFilter(filterKey);
  };

  const renderProductStatusFilters = () => {
    return (
      <Box sx={{ display: 'flex' }}>
        {filterValues.map((filterValue, i) => (
          <ProductStatusFilter
            key={i}
            heading={'heading'}
            loading={isTableDataLoading}
            text={filterValue.text}
            subText={String(numberOfItemsByStatus[filterValue.text as keyof typeof numberOfItemsByStatus])}
            isActive={filterValue.filterKey === filter}
            onClick={() => handleStatusFilterClick(filterValue.filterKey)}
            filterKey={filter}
          />
        ))}
      </Box>
    );
  };

  const renderDocumentToDistribute = () => {
    return (
      <Grid
        container
        sx={{
          display: 'flex',
          padding: '0.5em',
          border: '1px solid blue',
          backgroundColor: '#EAF2FF',
        }}
        data-testid="products-services-document-to-distribute"
      >
        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <Grid
            sx={{
              fontWeight: '700',
              color: jbColors.core.reflexBlue[100],
              display: 'flex',
            }}
          >
            {' '}
            <Box sx={{ p: 1.5 }}>
              <Typography>{'Open documents to distribute'}</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: '2em',
                  height: '2em',
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  backgroundColor: jbColors.core.reflexBlue[100],
                }}
              >
                <Typography sx={{ color: jbColors.core.stone[10] }}>
                  {documentToDistribute?.flatMap((d) => (d as { documents?: unknown[] })?.documents)?.length ?? '0'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid
          item
          xs={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            cursor: 'pointer',
            flexDirection: 'row',
          }}
        >
          <Button
            sx={{
              p: 0.5,
              cursor: 'pointer',
              border: '1px solid blue',
              backgroundColor: 'white',
              ':hover': {
                backgroundColor: jbColors.core.reflexBlue[100],
                color: 'white',
              },
            }}
            onClick={() => setOpen(true)}
          >
            Open documents to distribute
          </Button>
        </Grid>
      </Grid>
    );
  };

  return isTableDataLoading ? (
    <LoadingSpinner message={'Loading configured products and services.'} />
  ) : (
    <StickyHeaderWrapper
      hasStickyBottomBar={false}
      side="left"
      topComponents={
        <Box data-testid="products-services" sx={{ mr: 3, mb: 4 }}>
          <Typography variant="h2" sx={{ mb: 3 }}>
            {' '}
            {title}{' '}
          </Typography>
          {renderProductStatusFilters()}
          {documentToDistribute?.length > 0 && renderDocumentToDistribute()}
        </Box>
      }
      centerComponents={
        <>
          <ProductAndServicesTable
            filter={filter}
            selectedItem={selectedServiceAgreement}
            itemsToDisplay={itemsToDisplay}
            partnerId={partnerId}
            brUid={brUid}
            isLoading={isTableDataLoading}
          />
          <ProductDocumentsToDistributeDialog
            brUid={brUid}
            partnerId={partnerId}
            itemsToDistribute={documentToDistribute as DistributeItem[]}
            isOpen={open}
            setIsOpen={setOpen}
          />
        </>
      }
    />
  );
};
